//LIBRARIES
import React, { Fragment } from 'react';
import styled from 'styled-components';
import isEqual from 'lodash/isEqual';
import isEmpty from 'lodash/isEmpty';

//HELPERS
import { getTranslatedString } from 'helpers/i18n';

//COMPONENTS
import Spacer from 'components/ui/Spacer';
import TextBox from 'components/ui/TextBox';
import Form from 'components/ui/Form';
import Row from 'components/ui/Row';
import Col from 'components/ui/Col';
import Select from 'components/ui/SelectField';
import ReactPhoneInput from 'components/shared/ReactPhoneInput';

const Title = styled.p`
  font-size: 18px;
  font-weight: bold;
`;

const renderMaintenanceUserDetails = (values, errors, setFieldValue, handleChange) => (
  <Fragment>
    <Spacer mb={1}>
      <Row style={{ marginBottom: '12px' }}>
        <span>{getTranslatedString('landlordPortal.events.requiredField')}</span>
      </Row>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            label={`${getTranslatedString('landlordPortal.users.form.firstName')}*`}
            validateStatus={errors.first_name && 'error'}
            help={errors.first_name}
          >
            <TextBox
              name="first_name"
              value={values.first_name}
              onChange={handleChange}
              placeholder="e.g. John"
              size="large"
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label={`${getTranslatedString('landlordPortal.users.form.lastName')}*`}
            validateStatus={errors.last_name && 'error'}
            help={errors.last_name}
          >
            <TextBox
              name="last_name"
              value={values.last_name}
              onChange={handleChange}
              placeholder="e.g. Doe"
              size="large"
            />
          </Form.Item>
        </Col>
      </Row>
    </Spacer>
    <Spacer mb={1}>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            label={`${getTranslatedString('landlordPortal.users.form.email')}*`}
            validateStatus={errors.email && 'error'}
            help={errors.email}
          >
            <TextBox
              name="email"
              value={values.email}
              onChange={handleChange}
              placeholder="e.g. johndoe@gmail.com"
              size="large"
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label={`${getTranslatedString('landlordPortal.users.form.mobileNumber')}*`}
            validateStatus={errors.phone_number && 'error'}
            help={errors.phone_number}
          >
            <ReactPhoneInput
              value={values.phone_number}
              onChange={(value) => setFieldValue('phone_number', value)}
              hasError={errors.phone_number}
            />
          </Form.Item>
        </Col>
      </Row>
    </Spacer>
  </Fragment>
);

const renderAgentUserDetails = (values, errors, setFieldValue, handleChange) => {
  const customRole = {
    value: 1,
    label: 'Custom',
  };

  const roles = !isEmpty(values.roles) ? values.roles : [customRole];

  return (
    <Fragment>
      <Spacer mb={1}>
        <Title>{getTranslatedString('landlordPortal.users.form.userDetails')}</Title>
        <Row style={{ marginBottom: '12px' }}>
          <span>{getTranslatedString('landlordPortal.events.requiredField')}</span>
        </Row>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label={`${getTranslatedString('landlordPortal.users.form.firstName')}*`}
              validateStatus={errors.first_name && 'error'}
              help={errors.first_name}
            >
              <TextBox
                name="first_name"
                value={values.first_name}
                onChange={handleChange}
                placeholder="e.g. John"
                size="large"
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label={`${getTranslatedString('landlordPortal.users.form.lastName')}*`}
              validateStatus={errors.last_name && 'error'}
              help={errors.last_name}
            >
              <TextBox
                name="last_name"
                value={values.last_name}
                onChange={handleChange}
                placeholder="e.g. Doe"
                size="large"
              />
            </Form.Item>
          </Col>
        </Row>
      </Spacer>
      <Spacer mb={1}>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label={`${getTranslatedString('landlordPortal.users.form.email')}*`}
              validateStatus={errors.email && 'error'}
              help={errors.email}
            >
              <TextBox
                name="email"
                value={values.email}
                onChange={handleChange}
                placeholder="e.g. johndoe@gmail.com"
                size="large"
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label={`${getTranslatedString('landlordPortal.users.form.role')}*`}
              validateStatus={errors.role && 'error'}
              help={errors.role}
            >
              <Select
                name="role"
                onChange={(value) => setFieldValue('role', value)}
                value={values.role}
                size="large"
              >
                {roles.map((role) => (
                  <Select.Option key={`role_${role.value}`} value={role.value}>
                    {role.label}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>
      </Spacer>
      <Spacer mb={1}>
        <Row gutter={16}>
          <Col>
            <Form.Item
              label={`${getTranslatedString('landlordPortal.users.form.mobileNumber')}*`}
              validateStatus={errors.mobile_number && 'error'}
              help={errors.mobile_number}
            >
              <ReactPhoneInput
                value={values.mobile_number}
                onChange={(value) => setFieldValue('mobile_number', value)}
                hasError={errors.mobile_number}
              />
            </Form.Item>
          </Col>
        </Row>
      </Spacer>
      <Spacer mb={1}>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label={`${getTranslatedString('landlordPortal.users.form.officeNumber')}*`}
              validateStatus={errors.office_number && 'error'}
              help={errors.office_number}
            >
              <ReactPhoneInput
                value={values.office_number}
                onChange={(value) => setFieldValue('office_number', value)}
                hasError={errors.office_number}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Ext."
              validateStatus={errors.office_number_extension && 'error'}
              help={errors.office_number_extension}
            >
              <TextBox
                name="office_number_extension"
                value={values.office_number_extension}
                onChange={handleChange}
                placeholder="5233"
                size="large"
              />
            </Form.Item>
          </Col>
        </Row>
      </Spacer>
    </Fragment>
  );
};

const UserDetails = ({ values, errors, setFieldValue, handleChange }) => {
  const renderUserDetailsBody = isEqual(values.user_type, 'Agent')
    ? renderAgentUserDetails(values, errors, setFieldValue, handleChange)
    : renderMaintenanceUserDetails(values, errors, setFieldValue, handleChange);

  return <Fragment>{renderUserDetailsBody}</Fragment>;
};

export default UserDetails;
